import { useSyncExternalStore } from 'react';
import { de } from './de';
import type { MessageKey, Messages } from './en';
import { en } from './en';
import { es } from './es';
import { fr } from './fr';
import { nb } from './nb';
import { sv } from './sv';

/**
 * The app's languages: the same six as the website (iptviva.com). The choice is kept on the device;
 * the first time, it comes from the website page the visitor came from (iptviva.com/fr/… → French),
 * else the phone or browser language, else English.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'nb', name: 'Norsk' },
  { code: 'sv', name: 'Svenska' },
] as const;

export type Language = (typeof LANGUAGES)[number]['code'];

const MESSAGES: Record<Language, Messages> = { en, fr, es, de, nb, sv };
const LANGUAGE_KEY = 'viva.language';
const MARKET_KEY = 'viva.market';

/** The website's stores (iptviva.com/<store>/…): their language and country. */
const STORES: Record<string, { language: Language; country: string }> = {
  us: { language: 'en', country: 'US' },
  ca: { language: 'en', country: 'CA' },
  uk: { language: 'en', country: 'GB' },
  fr: { language: 'fr', country: 'FR' },
  es: { language: 'es', country: 'ES' },
  de: { language: 'de', country: 'DE' },
  no: { language: 'nb', country: 'NO' },
  se: { language: 'sv', country: 'SE' },
};

const isLanguage = (value: unknown): value is Language => LANGUAGES.some((entry) => entry.code === value);

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Without storage the choice lasts until the app closes.
  }
}

/** The website store the visitor came from (iptviva.com/fr/trial → fr), when the browser passed the page along. */
export function websiteStore(referrer = typeof document === 'undefined' ? '' : document.referrer): { language: Language; country: string } | null {
  try {
    const page = new URL(referrer);
    if (page.hostname !== 'iptviva.com' && page.hostname !== 'www.iptviva.com') return null;
    return STORES[page.pathname.split('/')[1] ?? ''] ?? null;
  } catch {
    return null;
  }
}

/** The first match for the phone or browser languages ("nn-NO" and "no" count as Norwegian). */
export function browserLanguage(languages: readonly string[]): Language | null {
  for (const entry of languages) {
    const code = entry.toLowerCase().split(/[-_]/)[0] ?? '';
    const mapped = code === 'no' || code === 'nn' ? 'nb' : code;
    if (isLanguage(mapped)) return mapped;
  }
  return null;
}

function detect(): Language {
  const saved = stored(LANGUAGE_KEY);
  const fromSite = websiteStore();
  if (fromSite) {
    // Coming from the website: its language and country win, and are kept.
    store(LANGUAGE_KEY, fromSite.language);
    store(MARKET_KEY, fromSite.country);
    return fromSite.language;
  }
  if (isLanguage(saved)) return saved;
  return browserLanguage(typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])) ?? 'en';
}

let current: Language = typeof window === 'undefined' ? 'en' : detect();
const listeners = new Set<() => void>();
if (typeof document !== 'undefined') document.documentElement.lang = current;

/** The country of the website store the visitor came from (e.g. "FR"), for the WhatsApp number's country code. */
export function market(): string | null {
  return stored(MARKET_KEY);
}

export function language(): Language {
  return current;
}

export function setLanguage(next: Language): void {
  if (next === current) return;
  current = next;
  store(LANGUAGE_KEY, next);
  document.documentElement.lang = next;
  for (const listener of listeners) listener();
}

/** A text in the current language; `{name}` placeholders are filled from `vars`. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const text = MESSAGES[current][key] ?? en[key];
  return vars ? text.replace(/\{(\w+)\}/g, (whole, name: string) => (name in vars ? String(vars[name]) : whole)) : text;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** The current language; components using it re-render when it changes. */
export function useLanguage(): Language {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** `t`, in a component that re-renders when the language changes. */
export function useT(): typeof t {
  useLanguage();
  return t;
}

const displayNames = new Map<string, Intl.DisplayNames | null>();
function names(type: 'region' | 'language'): Intl.DisplayNames | null {
  const key = `${current}:${type}`;
  if (!displayNames.has(key)) {
    try {
      displayNames.set(key, new Intl.DisplayNames([current === 'nb' ? 'nb' : current], { type }));
    } catch {
      displayNames.set(key, null);
    }
  }
  return displayNames.get(key) ?? null;
}

/** A country's name in the current language (the English one where the browser has no translation). */
export function countryName(iso: string, english: string): string {
  return (current === 'en' ? null : names('region')?.of(iso)) ?? english;
}

/** The channel-list languages are stored by English name ("Spanish"): their codes, to show them translated. */
const CATALOG_CODES: Record<string, string> = {
  Arabic: 'ar', French: 'fr', English: 'en', Spanish: 'es', German: 'de', Italian: 'it', Portuguese: 'pt', Turkish: 'tr', Dutch: 'nl',
  Polish: 'pl', Romanian: 'ro', Greek: 'el', Albanian: 'sq', Russian: 'ru', Hindi: 'hi', Urdu: 'ur', Persian: 'fa', Swedish: 'sv',
  Danish: 'da', Norwegian: 'no', Finnish: 'fi', Korean: 'ko',
};

/** A channel-list language ("Spanish") in the current language ("Español", "Espagnol"…). */
export function languageName(english: string): string {
  const code = CATALOG_CODES[english];
  if (!code || current === 'en') return english;
  const name = names('language')?.of(code);
  return name ? name.charAt(0).toLocaleUpperCase(current) + name.slice(1) : english;
}
